import React, { useState, useEffect, useMemo, useCallback, createContext } from 'react';
import { Coffee } from 'lucide-react';
import sites from './sites.json';
import weeks from './weeks.json';
// import Papa from 'papaparse';

import { Switch } from './@/components/ui/switch';
import { Label } from './@/components/ui/label';
import './App.css';
import './FileUpload.css';
import './SelectionControls.css';
import TeamOverview from './TeamOverview';
import ShiftDetails from './ShiftDetails';
// import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './@/components/ui/accordion';
import ConfigPanel from './ConfigPanel';
import CustomRulesPanel from './CustomRulesPanel';
import SchedulingLinksPanel from './SchedulingLinksPanel';
import { Loader, Download, Upload, Calendar, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './@/components/ui/select';
import { Alert, AlertDescription } from './@/components/ui/alert';
import ScheduleScores from './ScheduleScores';
import ScheduleGrid from './ScheduleGrid';
import RotaComplianceTable from './RotaComplianceTable';
import SetShiftOutcomesPanel from './SetShiftOutcomesPanel';
import RedistributionPanel from './RedistributionPanel';
import { StretchDayStrip, StretchLegend } from './StretchProfile';
import { SkillFloorDayStrip } from './SkillFloorStrip';
import Login from './Login';
import { displayRole, displayCompositeRole } from './roleLabel';
import { originalDemandBlocks } from './bandDemand';
export const partnerContext = createContext(null)

const testName = 'a_test';

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// Derive ordered day-of-week list from the earliest `date` in shape_of_day rows.
// Used so the rota view starts on whatever day the schedule week begins on
// (e.g. Sunday for moto, Monday for others).
const deriveOrderedDaysFromShape = (shapeRows) => {
  if (!Array.isArray(shapeRows) || shapeRows.length === 0) return null;
  const dates = shapeRows.map(r => r && r.date).filter(Boolean).sort();
  if (!dates.length) return null;
  const [y, m, d] = dates[0].split('-').map(Number);
  if (!y || !m || !d) return null;
  const startIdx = new Date(y, m - 1, d).getDay();
  return Array.from({ length: 7 }, (_, i) => DAY_NAMES[(startIdx + i) % 7]);
};

const ScheduleDashboard = () => {
  const [dev, setDev] = useState(process.env.REACT_APP_PROD === 'FALSE');
  const [partnerId, setPartnerId] = useState(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [shapeOfDay, setShapeOfDay] = useState([]);
  const [scheduledShifts, setScheduledShifts] = useState([]);
  const [events, setEvents] = useState([]);
  const [scheduleLoaded, setScheduleLoaded] = useState(false);
  const [team, setTeam] = useState([]);
  const [currentShiftIndex, setCurrentShiftIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [coverage, setCoverage] = useState({});
  const [totalCoverage, setTotalCoverage] = useState({ under: 0, over: 0, correct: 0 });
  const [scheduleScores, setScheduleScores] = useState({});
  const [selectedShift, setSelectedShift] = useState(null);
  const [scheduleAssignmentReasons, setScheduleAssignmentReasons] = useState({});
  const [unfilledKeyHolderShifts, setUnfilledKeyHolderShifts] = useState([]);
  const [keyHolderRescues, setKeyHolderRescues] = useState([]);
  const [unfilledSectionShifts, setUnfilledSectionShifts] = useState([]);
  const [sectionKeyHolderRescues, setSectionKeyHolderRescues] = useState([]);
  const [contractedShortfall, setContractedShortfall] = useState([]);
  const [stretchProfile, setStretchProfile] = useState(null);
  const [skillMixFloor, setSkillMixFloor] = useState(null);
  const [setShiftOutcomes, setSetShiftOutcomes] = useState([]);
  const [redistribution, setRedistribution] = useState([]);
  const [isGeneratingSchedule, setIsGeneratingSchedule] = useState(false);
  const [scheduleGenerated, setScheduleGenerated] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [assignedHours, setAssignedHours] = useState({});
  const [recentlyChangedHours, setRecentlyChangedHours] = useState({});
  const [isInitialView, setIsInitialView] = useState(true);
  const [config, setConfig] = useState({});
  const [busyPeriods, setBusyPeriods] = useState({});
  const [assignedShifts, setAssignedShifts] = useState({shifts: 0, days: 0});
  const [stintEnabled, setStintEnabled] = useState(true);
  const [orderedDays, setOrderedDays] = useState([]);
  const [roleOrder, setRoleOrder] = useState([]);
  const [breaks, setBreaks] = useState({});
  const [shape_of_day_types, setShape_of_day_types] = useState([]);
  const [headcountDiffMap, setHeadcountDiffMap] = useState({});
  const [slotDiffMap, setSlotDiffMap] = useState({});
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('');
  const [fromDate, setFromDate] = useState(() => {
    // Initialize to today's date
    const today = new Date();
    return today.toISOString().split('T')[0]; // YYYY-MM-DD format
  });
  const [toDate, setToDate] = useState(() => {
    // Initialize to today's date
    const today = new Date();
    return today.toISOString().split('T')[0]; // YYYY-MM-DD format
  });
  const [fromTime, setFromTime] = useState('09:00');
  const [toTime, setToTime] = useState(() => {
    // Initialize to current time, rounded to nearest 15 minutes
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = (Math.ceil(now.getMinutes() / 15) * 15) % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [manualPartnerNodeId, setManualPartnerNodeId] = useState('');
  const [isDebuggingMode, setIsDebuggingMode] = useState(false);
  const [customRules, setCustomRules] = useState([]);
  const [schedulingLinks, setSchedulingLinks] = useState([]);
  const [labourBands, setLabourBands] = useState(null);
  const [breakRules, setBreakRules] = useState(null);
  const [customRuleResults, setCustomRuleResults] = useState(null);
  const [availableShapes, setAvailableShapes] = useState([]);
  const [selectedShape, setSelectedShape] = useState(null);
  const [isRotaExpanded, setIsRotaExpanded] = useState(true);
  const [isTeamExpanded, setIsTeamExpanded] = useState(true);
  const [isShapeOfDayExpanded, setIsShapeOfDayExpanded] = useState(false);
  const [isScoresExpanded, setIsScoresExpanded] = useState(false);
  const [idealMatchEnabled, setIdealMatchEnabled] = useState(true);
  const [idealMatchBuffer, setIdealMatchBuffer] = useState(1.5);
  const [boundaryMatchEnabled, setBoundaryMatchEnabled] = useState(true);
  const [boundaryMatchBuffer, setBoundaryMatchBuffer] = useState(1.5);
  const [forecast, setForecast] = useState(null);
  const [hourlyForecasts, setHourlyForecasts] = useState(null);

  // Utility function to format date as YYYY-MM-DD
  const formatDateForInput = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Validate test request fields
  const validateTestRequestFields = useCallback(() => {
    const errors = {};
    
    if (isManualEntry) {
      if (!manualPartnerNodeId.trim()) {
        errors.site = "Please enter a partner node ID";
      }
    } else {
      if (!selectedSite) {
        errors.site = "Please select a site";
      }
    }
    
    if (!selectedWeek) {
      errors.week = "Please select a week";
    }
    
    if (!fromDate) {
      errors.fromDate = "Please select a From date";
    }
    
    if (!toDate) {
      errors.toDate = "Please select a To date";
    }
    
    // Validate dates make sense
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      
      if (from > to) {
        errors.dateRange = "From date must be before To date";
      }
    }
    
    if (!fromTime) {
      errors.fromTime = "Please select a From time";
    }
    
    if (!toTime) {
      errors.toTime = "Please select a To time";
    }
    
    // Validate times if on same day
    if (fromDate && toDate && fromTime && toTime && fromDate === toDate) {
      const fromDateTime = new Date(`${fromDate}T${fromTime}`);
      const toDateTime = new Date(`${toDate}T${toTime}`);
      
      if (fromDateTime >= toDateTime) {
        errors.timeRange = "From time must be before To time on the same day";
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [selectedSite, selectedWeek, fromDate, toDate, fromTime, toTime, isManualEntry, manualPartnerNodeId]);

  // Run validation on field changes
  useEffect(() => {
    validateTestRequestFields();
  }, [selectedSite, selectedWeek, fromDate, toDate, fromTime, toTime, isManualEntry, manualPartnerNodeId, validateTestRequestFields]);

  // Generate past and future weeks for selection
  const getRelevantWeeks = () => {
    const today = new Date();
    const monday = new Date(today);
    // Adjust to previous Monday if needed
    monday.setDate(monday.getDate() - (monday.getDay() === 0 ? 6 : monday.getDay() - 1));
    
    const weeks = [];
    
    // Add 4 past weeks
    for (let i = 4; i > 0; i--) {
      const pastMonday = new Date(monday);
      pastMonday.setDate(monday.getDate() - (7 * i));
      weeks.push(formatDateForInput(pastMonday));
    }
    
    // Add current week
    weeks.push(formatDateForInput(monday));
    
    // Add 4 future weeks
    for (let i = 1; i <= 4; i++) {
      const futureMonday = new Date(monday);
      futureMonday.setDate(monday.getDate() + (7 * i));
      weeks.push(formatDateForInput(futureMonday));
    }
    
    return weeks;
  };

  // Handle site selection change
  const handleSiteChange = (value) => {
    if (value === 'manual_entry') {
      setIsManualEntry(true);
      setSelectedSite('');
      setManualPartnerNodeId('');
    } else {
      setIsManualEntry(false);
      setSelectedSite(value);
      setManualPartnerNodeId('');
    }
  };

  const handleLogin = (newPartnerId) => {
    setPartnerId(newPartnerId);
    setIsLoggedIn(true);
  };

  const parseCSV = useCallback((text) => {
    console.log('Parsing CSV:', text); // Add this line to log the input
    const lines = text.trim().split('\n');
    const headers = lines[0].split(',').map(header => header.trim());
    
    return lines.slice(1).map(line => {
      // Trim the line itself to remove potential trailing \r before splitting
      const values = line.trim().split(','); 
      return headers.reduce((obj, header, index) => {
        // Ensure value exists and trim it
        let value = values[index] ? values[index].trim() : undefined; 
        
        // Parse numeric values
        // Only attempt parsing if value is not undefined and not an empty string
        if (value !== undefined && value !== '' && !isNaN(value)) {
          value = parseFloat(value);
        }
        
        // Apply formatTime to 'start' and 'end' columns
        if (header === 'start' || header === 'end') {
          // Ensure value is defined before formatting
          if (value !== undefined) { 
            value = formatTime(value);
          }
        }
        
        // Parse shift_roles column if it contains a string representation of a list
        if (header === 'shift_roles' && typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) { 
          try {
            value = JSON.parse(value); 
          } catch (error) {
            console.error('Error parsing shift_roles:', error);
            // Fallback to simple string parsing if JSON.parse fails
            value = value.replace(/[\[\]"']/g, '').split(',').map(item => item.trim());
          }
        }
        
        obj[header] = value; 
        return obj;
      }, {});
    });
  }, []);

  const importTeamData = useCallback(async (partnerId, shapeName = null) => {
    if (!partnerId) return;
    try {
      let teamModule;
      if (shapeName) {
        try {
          teamModule = await import(`./defaults/partner/${partnerId}/UK/${shapeName}/team.json`);
          console.log(`Using ${shapeName} team data`);
        } catch (error) {
          console.log(`${shapeName} team not found, falling back to base`);
        }
      }
      if (!teamModule) {
        teamModule = await import(`./defaults/partner/${partnerId}/UK/team.json`);
      }
      setTeam(teamModule.default);
      console.log("Team:", teamModule.default);
    } catch (error) {
      console.error('Error loading team data:', error);
    }
  }, []);

  const importForecastData = useCallback(async (partnerId, shapeName = null) => {
    try {
      let mod;
      if (shapeName) {
        try {
          mod = await import(`./defaults/partner/${partnerId}/UK/${shapeName}/forecast.json`);
        } catch (e) { /* fall back below */ }
      }
      if (!mod) {
        try {
          mod = await import(`./defaults/partner/${partnerId}/UK/forecast.json`);
        } catch (e) { setForecast(null); return; }
      }
      setForecast(mod.default || mod);
    } catch (e) {
      setForecast(null);
    }
  }, []);

  const importConfigData = useCallback(async (partnerId, shapeName = null) => {
    try {
      let configModule;
      if (shapeName) {
        try {
          configModule = await import(`./defaults/partner/${partnerId}/UK/${shapeName}/config.json`);
          console.log(`Using ${shapeName} config`);
        } catch (error) {
          console.log(`${shapeName} config not found, falling back to base`);
        }
      }
      if (!configModule) {
        configModule = await import(`./defaults/partner/${partnerId}/UK/config.json`);
      }
      setConfig(configModule.default);
      setOrderedDays(configModule.default.ORDERED_DAYS);
      setLabourBands(null);
      // Partner-default configs carry their own BREAKS tiers; drop any fixture's
      // breakRules so they can't override the config we just loaded.
      setBreakRules(null);
      console.log("Config:", configModule.default);
    } catch (error) {
      console.error('Error loading config data:', error);
      setIsLoggedIn(false);
    }
  }, []);

  const importShapeOfDayData = useCallback(async (partnerId, stintEnabled, shapeName = null) => {
    try {
      let shapeOfDayPath;

      if (partnerId) {
        // Try subfolder first if shapeName is provided
        if (shapeName) {
          try {
            const shapeOfDayModule = stintEnabled
              ? await import(`./defaults/partner/${partnerId}/UK/${shapeName}/shape_of_day.csv`)
              : await import(`./defaults/partner/${partnerId}/UK/${shapeName}/no_stint_shape_of_day.csv`);
            shapeOfDayPath = shapeOfDayModule.default;
            console.log(`Using ${shapeName} shape of day`);
          } catch (error) {
            console.log(`${shapeName} shape of day not found, falling back to base`);
          }
        }

        // Fall back to base if no subfolder path resolved
        if (!shapeOfDayPath) {
          try {
            const shapeOfDayModule = stintEnabled
              ? await import(`./defaults/partner/${partnerId}/UK/shape_of_day.csv`)
              : await import(`./defaults/partner/${partnerId}/UK/no_stint_shape_of_day.csv`);
            shapeOfDayPath = shapeOfDayModule.default;
            console.log('Using base shape of day files');
          } catch (error) {
            console.log('Base shape of day files not found');
          }
        }
      } else {
        console.log('Error loading shape of day - no partner ID');
      }

      if (!shapeOfDayPath) return;

      const shapeOfDayResponse = await fetch(shapeOfDayPath);
      const shapeOfDayCSV = await shapeOfDayResponse.text();
      const shapeOfDayData = parseCSV(shapeOfDayCSV);

      setShapeOfDay(shapeOfDayData);
      const derived = deriveOrderedDaysFromShape(shapeOfDayData);
      if (derived) setOrderedDays(derived);
      setScheduleGenerated(true);
      console.log('Shape of day data loaded successfully');
    } catch (error) {
      console.error('Error loading shape of day data:', error);
    }
  }, [parseCSV]);

  const importEventsData = useCallback(async (partnerId, shapeName = null) => {
    if (!partnerId) return;
    let eventsPath;
    try {
      if (shapeName) {
        try {
          const eventsModule = await import(`./defaults/partner/${partnerId}/UK/${shapeName}/events.csv`);
          eventsPath = eventsModule.default;
          console.log(`Using ${shapeName} events`);
        } catch (error) {
          console.log(`${shapeName} events not found, falling back to base`);
        }
      }
      if (!eventsPath) {
        const eventsModule = await import(`./defaults/partner/${partnerId}/UK/events.csv`);
        eventsPath = eventsModule.default;
      }
      const eventsResponse = await fetch(eventsPath);
      const eventsCSV = await eventsResponse.text();
      const eventsData = parseCSV(eventsCSV);
      setEvents(eventsData);
    } catch (error) {
      console.log('Events not found or could not be loaded:', error);
      setEvents([]);
    }
  }, [parseCSV]);

  const importCustomRulesData = useCallback(async (partnerId, shapeName = null) => {
    if (!partnerId) return;
    const storageKey = `customRules_${partnerId}_${shapeName || 'base'}`;
    // Check localStorage first
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setCustomRules(parsed);
        console.log("Custom Rules (from localStorage):", parsed);
        return;
      } catch (e) {
        console.log('Invalid localStorage custom rules, falling back to file');
      }
    }
    // Try subfolder first, fall back to base
    if (shapeName) {
      try {
        const rulesModule = await import(`./defaults/partner/${partnerId}/UK/${shapeName}/custom_rules.json`);
        setCustomRules(rulesModule.default);
        console.log(`Custom Rules (from ${shapeName}):`, rulesModule.default);
        return;
      } catch (error) {
        console.log(`${shapeName} custom rules not found, falling back to base`);
      }
    }
    try {
      const rulesModule = await import(`./defaults/partner/${partnerId}/UK/custom_rules.json`);
      setCustomRules(rulesModule.default);
      console.log("Custom Rules:", rulesModule.default);
    } catch (error) {
      console.log('No custom rules found for partner:', partnerId);
      setCustomRules([]);
    }
  }, []);

  const importManifest = useCallback(async (partnerId) => {
    if (!partnerId) return;
    try {
      const manifestModule = await import(`./defaults/partner/${partnerId}/UK/manifest.json`);
      const shapes = manifestModule.default.shapes || [];
      setAvailableShapes(shapes);
      if (shapes.length > 0) {
        setSelectedShape(shapes[0]);
      }
    } catch (error) {
      console.log('No manifest found for partner:', partnerId);
      setAvailableShapes([]);
      setSelectedShape(null);
    }
  }, []);

  // Persist custom rules to localStorage whenever they change
  // Note: selectedShape is intentionally read but not in deps — we only want to
  // save when customRules actually changes (user edits or new load), not when
  // the shape selector changes (which would save old rules under the new key).
  useEffect(() => {
    if (partnerId && customRules) {
      const storageKey = `customRules_${partnerId}_${selectedShape || 'base'}`;
      localStorage.setItem(storageKey, JSON.stringify(customRules));
    }
  }, [customRules, partnerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (partnerId) {
      console.log('Importing config data for partner:', partnerId);
      importConfigData(partnerId);
      importCustomRulesData(partnerId);
      importManifest(partnerId);
    }
  }, [partnerId, importConfigData, importCustomRulesData, importManifest]);

  // Reload all data when selectedShape changes
  useEffect(() => {
    if (partnerId && selectedShape) {
      // Reset schedule state so stale data from the previous week doesn't persist
      setScheduledShifts([]);
      setScheduleLoaded(false);
      setScheduleGenerated(false);
      setCoverage({});
      setTotalCoverage({ under: 0, over: 0, correct: 0 });
      setScheduleScores({});
      setScheduleAssignmentReasons({});
      setUnfilledKeyHolderShifts([]);
      setKeyHolderRescues([]);
      setUnfilledSectionShifts([]);
      setSectionKeyHolderRescues([]);
      setContractedShortfall([]);
      setSetShiftOutcomes([]);
      setRedistribution([]);
      setBusyPeriods({});
      setBreaks({});

      importShapeOfDayData(partnerId, stintEnabled, selectedShape);
      importEventsData(partnerId, selectedShape);
      importConfigData(partnerId, selectedShape);
      importTeamData(partnerId, selectedShape);
      importCustomRulesData(partnerId, selectedShape);
      importForecastData(partnerId, selectedShape);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- import functions are stable useCallback refs; including them would cause reload loops
  }, [selectedShape, partnerId, stintEnabled]);

  const handleFileUpload = useCallback((event, setFile) => {
    const file = event.target.files[0];
    console.log('File:', file);
    
    // If the file exists, log its path and name
    if (file) {
      // Get the file name and path (if available)
      const filePath = file.webkitRelativePath || file.name;
      console.log('File Name:', file.name);
      console.log('File Path:', filePath); // Sometimes only the name is available

      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;

        // Handling CSV file
        if (file.name.endsWith('.csv')) {
          const data = parseCSV(text);
          setScheduledShifts([]);
          setScheduleLoaded(false);
          setCoverage({});
          setTotalCoverage({ under: 0, over: 0, correct: 0 });
          setScheduleScores({});
          setScheduleAssignmentReasons({});
          setUnfilledKeyHolderShifts([]);
          setKeyHolderRescues([]);
          setUnfilledSectionShifts([]);
          setSectionKeyHolderRescues([]);
          setContractedShortfall([]);
          setSetShiftOutcomes([]);
          setRedistribution([]);
          setBusyPeriods({});
          setBreaks({});
          setShapeOfDay(data);
          const derived = deriveOrderedDaysFromShape(data);
          if (derived) setOrderedDays(derived);
          setScheduleGenerated(true);
        }
        // Handling JSON file
        else if (file.name.endsWith('.json')) {
          const data = JSON.parse(text);
          setScheduledShifts([]);
          setScheduleLoaded(false);
          setCoverage({});
          setTotalCoverage({ under: 0, over: 0, correct: 0 });
          setScheduleScores({});
          setScheduleAssignmentReasons({});
          setUnfilledKeyHolderShifts([]);
          setKeyHolderRescues([]);
          setUnfilledSectionShifts([]);
          setSectionKeyHolderRescues([]);
          setContractedShortfall([]);
          setSetShiftOutcomes([]);
          setRedistribution([]);
          setBusyPeriods({});
          setBreaks({});
          setTeam(data);
          setScheduleGenerated(true);
        }
      };
      reader.readAsText(file);
    }
  }, [parseCSV]);


  const handleUploadDefaults = useCallback(async () => {
    console.log('Loading default data');

    try {
      await importShapeOfDayData(partnerId, stintEnabled, selectedShape);
      await importEventsData(partnerId, selectedShape);
      await importConfigData(partnerId, selectedShape);
      console.log('Importing team data for partner:', partnerId);
      if (partnerId) {
        importTeamData(partnerId, selectedShape);
      }
      await importCustomRulesData(partnerId, selectedShape);

      console.log('Default data loading complete');
    } catch (error) {
      console.error('Error loading default CSV files:', error);
    }
  }, [importShapeOfDayData, importTeamData, importEventsData, importConfigData, importCustomRulesData, partnerId, stintEnabled, selectedShape]);

  // Derive role order from team data when team changes
  // Default: Manager, Waiter first, then the rest alphabetically
  useEffect(() => {
    if (team.length === 0) return;
    const roles = [...new Set(team.filter(m => m.Active !== false).map(m => m.Role_Type || 'Other'))];
    setRoleOrder(prev => {
      // Keep existing order, append any new roles
      const existing = prev.filter(r => roles.includes(r));
      const newRoles = roles.filter(r => !existing.includes(r));
      if (existing.length > 0) return [...existing, ...newRoles];
      // Default ordering: Manager, Waiter first, then rest alphabetically
      const priority = ['Manager','Shift Manager', 'Waiter','Kitchen'];
      const prioritized = priority.filter(r => roles.includes(r));
      const rest = roles.filter(r => !priority.includes(r)).sort();
      return [...prioritized, ...rest];
    });
  }, [team]);

  const handleUploadTestingData = useCallback(async () => {
    console.log('Loading Testing Data');

    try {
      // Fetch the CSV content from the URLs
      const shapeOfDayModule = await import(`./testing/${testName}/shape_of_day.csv`);
      const teamModule = await import(`./testing/${testName}/team.json`);
      
      const configModule = await import(`./testing/${testName}/config.json`);
      const eventsModule = await import(`./testing/${testName}/events.csv`);
      // Fetch the actual CSV content
      const shapeOfDayCSV = await fetch(shapeOfDayModule.default).then(response => response.text());
      const eventsCSV = await fetch(eventsModule.default).then(response => response.text());

      // Now pass the CSV content to parseCSV
      const shapeOfDayData = parseCSV(shapeOfDayCSV);
      const eventsData = parseCSV(eventsCSV);
      // Log the parsed data
      console.log('Parsed Shape of Day:', shapeOfDayData);
      console.log('Parsed Events:', eventsData);
      // Set the state with the parsed data
      setShapeOfDay(shapeOfDayData);
      setEvents(eventsData);
      setScheduleGenerated(true);

      // Set the team data
      console.log('Fetched Team:', teamModule.default);

      setTeam(teamModule.default);

      // Set the config data
      console.log('Fetched Config:', configModule.default);

      setConfig(configModule.default);
      setOrderedDays(configModule.default.ORDERED_DAYS);

      // Optional: only fixtures captured from a bands-enabled run have labour_bands.json.
      try {
        const labourBandsModule = await import(`./testing/${testName}/labour_bands.json`);
        setLabourBands(labourBandsModule.default);
        console.log('Fetched Labour Bands:', labourBandsModule.default);
      } catch (error) {
        setLabourBands(null);
        console.log('No labour_bands.json for this test fixture');
      }

      // Optional: only fixtures captured from a request that carried breakRules have
      // break_rules.json. Absent = the config's own BREAKS tiers stay authoritative,
      // which is what apply_break_rules() does server-side on a null/empty list.
      try {
        const breakRulesModule = await import(`./testing/${testName}/break_rules.json`);
        setBreakRules(breakRulesModule.default);
        console.log('Fetched Break Rules:', breakRulesModule.default);
      } catch (error) {
        setBreakRules(null);
        console.log('No break_rules.json for this test fixture');
      }

      // Per-slot item forecasts — the numerator for competency-under-stretch. Optional:
      // only fixtures captured from a request that carried them have this file.
      try {
        const hourlyForecastsModule = await import(`./testing/${testName}/hourly_forecasts.json`);
        setHourlyForecasts(hourlyForecastsModule.default);
        console.log('Fetched Hourly Forecasts:', hourlyForecastsModule.default);
      } catch (error) {
        setHourlyForecasts(null);
        console.log('No hourly_forecasts.json for this test fixture');
      }

      console.log('Default data loading complete');
    } catch (error) {
      console.error('Error loading default CSV files:', error);
    }
  }, [parseCSV, setShapeOfDay, setScheduleGenerated, importEventsData, testName]);
  
  
  

  const handleDownloadSchedule = useCallback(() => {
    if (!scheduledShifts.length) return;
    const csvContent = [
      ['day', 'start', 'end', 'length', 'name', 'role', 'shift_type', 'shape_of_day_type'].join(','),
      ...scheduledShifts.map(shift => 
        [shift.day, shift.start, shift.end, shift.length, shift.name, shift.role, shift.shift_type, shift.shape_of_day_type].join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'generated_schedule.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [scheduledShifts]);

  const handleScheduledShiftsUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) {
      console.log('No file selected');
      return; // Exit the function if no file is selected
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const data = parseCSV(text);
      setScheduledShifts(data);
      setScheduleLoaded(true);
    };
    reader.readAsText(file);
  }, [parseCSV]);


  const formatTime = (time) => {
    // If it's already in "HH:MM" format, return it as is
    if (typeof time === 'string' && time.includes(':')) {
      return time;
    }
  
    // Convert to number if it's a string representation of a number
    const numTime = typeof time === 'string' ? parseFloat(time) : time;
  
    // Check if it's a valid number
    if (isNaN(numTime)) {
      console.error('Invalid time format:', time);
      return '00:00'; // or some default value
    }
  
    const hours = Math.floor(numTime);
    const minutes = Math.round((numTime - hours) * 60);
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const parseTime = useCallback((timeStr) => {
    if (typeof timeStr !== 'string') {
      return timeStr;
    }
    if (timeStr.includes(':')) {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours + minutes / 60;
    }
    return parseFloat(timeStr);
  }, []);

  const visibleScheduledShifts = useMemo(() => {
    if (isInitialView) return scheduledShifts;
    if (currentShiftIndex === -1) return [];
    return scheduledShifts.slice(0, currentShiftIndex + 1);
  }, [scheduledShifts, currentShiftIndex, isInitialView]);

  const calculateAssignedShifts = useCallback((shifts) => {
    return shifts.reduce((acc, shift) => {
      if (shift.name !== "Stint" && shift.name !== "TBC") {
        if (!acc[shift.name]) {
          acc[shift.name] = { shifts: 0, days: new Set() };
        }
        acc[shift.name].shifts += 1;
        acc[shift.name].days.add(shift.day);
      }
      return acc;
    }, {});
  }, []);

  const calculateAssignedHours = useCallback((shifts) => {
    return shifts.reduce((acc, shift) => {
      if (shift.name !== "Stint" && shift.name !== "TBC") {
        acc[shift.name] = (acc[shift.name] || 0) + parseFloat(shift.length);
      }
      return acc;
    }, {});
  }, []);

  useEffect(() => {
    if (scheduleLoaded) {
      const hours = calculateAssignedHours(visibleScheduledShifts);
      const shifts = calculateAssignedShifts(visibleScheduledShifts);
      
      setAssignedHours(prevHours => {
        const changedHours = {};
        Object.keys(hours).forEach(name => {
          if (hours[name] !== prevHours[name]) {
            changedHours[name] = hours[name];
          }
        });
        setRecentlyChangedHours(changedHours);
        
        setTimeout(() => setRecentlyChangedHours({}), 1000);
        
        return hours;
      });
  
      setAssignedShifts(Object.keys(shifts).reduce((acc, name) => {
        acc[name] = { shifts: shifts[name].shifts, days: shifts[name].days.size };
        return acc;
      }, {shifts: 0, days: 0}));
    }
  }, [scheduleLoaded, visibleScheduledShifts, calculateAssignedHours, calculateAssignedShifts]);

  useEffect(() => {
    console.log('useEffect called with assignedHours and assignedShifts:', assignedHours, assignedShifts);
    const updatedTeam = team.map(member => ({
      ...member,
      Assigned_Hours: assignedHours[member.Name] || 0,
      Assigned_Shifts: assignedShifts[member.Name] || {shifts: 0, days: 0}
    }));
    setTeam(updatedTeam);
  }, [assignedHours, assignedShifts]);

  const handlePlayPause = useCallback(() => {
    if (isInitialView) {
      setIsInitialView(false);
      setCurrentShiftIndex(0);
    }
    setIsPlaying(prev => !prev);
  }, [isInitialView]);

  const handleReset = useCallback(() => {
    setCurrentShiftIndex(-1);
    setIsPlaying(false);
    setIsInitialView(true);
  }, []);

  const handleForward = useCallback(() => {
    if (isInitialView) {
      setIsInitialView(false);
      setCurrentShiftIndex(0);
    } else {
      setCurrentShiftIndex(prevIndex => Math.min(prevIndex + 1, scheduledShifts.length - 1));
    }
  }, [scheduledShifts, isInitialView]);

  const handleBack = useCallback(() => {
    if (currentShiftIndex > 0) {
      setCurrentShiftIndex(prevIndex => prevIndex - 1);
    } else if (currentShiftIndex === 0) {
      setIsInitialView(true);
      setCurrentShiftIndex(-1);
    }
  }, [currentShiftIndex]);

  useEffect(() => {
    let intervalId;
    if (isPlaying && currentShiftIndex < scheduledShifts.length - 1) {
      intervalId = setInterval(() => {
        setCurrentShiftIndex(prevIndex => {
          const newIndex = prevIndex + 1;
          if (newIndex >= scheduledShifts.length - 1) {
            setIsPlaying(false);
            return scheduledShifts.length - 1;
          }
          return newIndex;
        });
      }, 750);
    }
    return () => clearInterval(intervalId);
  }, [isPlaying, currentShiftIndex, scheduledShifts]);

  // Band-driven demand changes keyed like slotDiffMap ("day|shape"). Empty when the
  // loaded fixture carries no labour band.
  const bandDeltaMap = useMemo(() => {
    const map = {};
    (labourBands?.deltas || []).forEach((d) => {
      const key = `${d.day}|${d.shape}`;
      if (!map[key]) map[key] = [];
      map[key].push(d);
    });
    return map;
  }, [labourBands]);

  // Display-only: the demand grid draws the site's ORIGINAL demand, with the band's
  // changes rendered on top as Band +/− rows. The audit request still sends the
  // clamped shapeOfDay — only what the grid shows changes here.
  const displayShapeOfDay = useMemo(() => {
    const keys = Object.keys(bandDeltaMap);
    if (!keys.length) return shapeOfDay;
    const grouped = {};
    const out = [];
    shapeOfDay.forEach((b) => {
      const key = `${b.day}|${b.shape_of_day_type}`;
      if (bandDeltaMap[key]) (grouped[key] = grouped[key] || []).push(b);
      else out.push(b);
    });
    keys.forEach((key) => {
      const [day, shape] = [key.slice(0, key.indexOf('|')), key.slice(key.indexOf('|') + 1)];
      out.push(...originalDemandBlocks(grouped[key] || [], bandDeltaMap[key], { day, shape_of_day_type: shape }));
    });
    return out;
  }, [shapeOfDay, bandDeltaMap]);

  const organizedSchedule = useMemo(() => {
    const scheduleByDay = orderedDays.reduce((acc, day) => {
      const dayShape = displayShapeOfDay.filter(item => item.day === day);
      const daySchedule = visibleScheduledShifts.filter(item => item.day === day);
      
      if (dayShape.length > 0 || daySchedule.length > 0) {
        const processShifts = (shifts, isShapeOfDay) => {
          const processedShifts = shifts.map(shift => ({
            ...shift,
            start: parseTime(shift.start),
            end: parseTime(shift.end),
            startDisplay: shift.start,
            endDisplay: shift.end,
            length: parseTime(shift.end) - parseTime(shift.start),
            isShapeOfDay
          }));
  
          processedShifts.sort((a, b) => b.length - a.length);
  
          const rows = [];
          const shiftsOverlap = (shift1, shift2) => {
            return (shift1.start < shift2.end) && (shift2.start < shift1.end);
          };
  
          processedShifts.forEach(shift => {
            let placed = false;
            for (let i = 0; i < rows.length; i++) {
              if (rows[i].every(existingShift => !shiftsOverlap(shift, existingShift))) {
                rows[i].push(shift);
                placed = true;
                break;
              }
            }
            if (!placed) {
              rows.push([shift]);
            }
          });
  
          return rows;
        };
  
        // Group shifts by role
        const uniqueShape_of_day_types = [...new Set(dayShape.map(shift => 
          shift.shape_of_day_type || 'unknown'  // Provide default value if undefined
        ))];
        
        acc[day] = {};
  
        uniqueShape_of_day_types.forEach(shape_of_day_type => {
          if (!shape_of_day_type) return; // Skip if undefined
  
          const shape_of_day_typeShifts = dayShape.filter(shift => 
            shift.shape_of_day_type === shape_of_day_type
          );
          
          // Split role group by semicolon and trim each role
          const shape_of_day_types = shape_of_day_type.includes('+') 
            ? shape_of_day_type.split('+') 
            : [shape_of_day_type];
            
          // Add individual roles plus the full combined role group
          const shape_of_day_typesList = [...shape_of_day_types, shape_of_day_type];
          const shape_of_day_typeScheduleShifts = daySchedule.filter(shift => 
            shift.shape_of_day_type && shape_of_day_typesList.includes(shift.shape_of_day_type)
          );
  
          const shapeRows = processShifts(shape_of_day_typeShifts, true);
          const scheduleRows = processShifts(shape_of_day_typeScheduleShifts, false);
  
          acc[day][shape_of_day_type] = shapeRows.map((shapeRow, index) => {
            const scheduleRow = scheduleRows[index] || [];
            return [...shapeRow, ...scheduleRow];
          });
  
          // Add any remaining schedule rows
          for (let i = shapeRows.length; i < scheduleRows.length; i++) {
            acc[day][shape_of_day_type].push(scheduleRows[i]);
          }
        });
      }
      return acc;
    }, {});
  
    return scheduleByDay;
  }, [displayShapeOfDay, visibleScheduledShifts, orderedDays, parseTime]);

  // Lookup of honoured set-shift outcomes keyed by (employee_id, dayName, startHHMM).
  // Used to tag the matching scheduled shift with its outcome so ShiftCard can badge it.
  const honouredSetShiftLookup = useMemo(() => {
    const map = new Map();
    (setShiftOutcomes || []).forEach(o => {
      if (o.status !== 'HONOURED') return;
      const dayName = typeof o.day === 'number' ? orderedDays[o.day] : o.day;
      if (!dayName) return;
      map.set(`${o.employee_id}|${dayName}|${o.start}`, o);
    });
    return map;
  }, [setShiftOutcomes, orderedDays]);

  // Member-centric schedule: { memberName: { Monday: [shift, shift], Tuesday: [shift], ... } }
  const memberSchedule = useMemo(() => {
    const schedule = {};
    visibleScheduledShifts.forEach(shift => {
      if (!schedule[shift.name]) schedule[shift.name] = {};
      if (!schedule[shift.name][shift.day]) schedule[shift.name][shift.day] = [];
      const setShiftOutcome = shift.employee_id
        ? honouredSetShiftLookup.get(`${shift.employee_id}|${shift.day}|${shift.start}`)
        : null;
      schedule[shift.name][shift.day].push({
        ...shift,
        start: parseTime(shift.start),
        end: parseTime(shift.end),
        startDisplay: shift.start,
        endDisplay: shift.end,
        length: parseTime(shift.end) - parseTime(shift.start),
        isShapeOfDay: false,
        setShiftOutcome: setShiftOutcome || null,
      });
    });
    // Sort shifts within each day by start time
    Object.values(schedule).forEach(days => {
      Object.values(days).forEach(shifts => {
        shifts.sort((a, b) => a.start - b.start);
      });
    });
    return schedule;
  }, [visibleScheduledShifts, parseTime, honouredSetShiftLookup]);

  // Table rows grouped by Role_Type: [{ type: 'header', role }, { type: 'member', member, role }, ...]
  // Open shifts (Stint/TBC) are placed in the role group matching their shape_of_day_type.
  const tableRows = useMemo(() => {
    const roleGroups = {};
    team.forEach(member => {
      if (member.Active === false) return;
      const role = member.Role_Type || 'Other';
      if (!roleGroups[role]) roleGroups[role] = [];
      roleGroups[role].push(member);
    });

    // Build a map of open shift names per Role_Type
    // Each open name can appear under multiple roles (e.g. TBC under both Kitchen and Waiter)
    // Composite shape_of_day_types (with +) go to Waiter to avoid duplication
    const openShiftsByRole = {};
    const unmatchedRolesByName = {}; // track unmatched shape_of_day_types per open name
    const openNames = ['Stint', 'TBC'];
    openNames.forEach(name => {
      const shifts = memberSchedule[name];
      if (!shifts) return;
      const rolesForName = new Set();
      Object.values(shifts).flat().forEach(s => {
        if (s.shape_of_day_type) {
          if (s.shape_of_day_type.includes('+')) {
            // Composite shapes are interchangeable pools — pick a single target role.
            // Prefer Waiter, then Kitchen, then the first part of the composite.
            const parts = s.shape_of_day_type.split('+').map(p => p.trim());
            if (parts.includes('Waiter')) rolesForName.add('Waiter');
            else if (parts.includes('Kitchen')) rolesForName.add('Kitchen');
            else rolesForName.add(parts[0]);
          } else {
            rolesForName.add(s.shape_of_day_type);
          }
        }
      });
      rolesForName.forEach(role => {
        const matchedRole = Object.keys(roleGroups).find(r => r === role);
        if (matchedRole) {
          if (!openShiftsByRole[matchedRole]) openShiftsByRole[matchedRole] = new Set();
          openShiftsByRole[matchedRole].add(name);
        } else {
          if (!openShiftsByRole['Open']) openShiftsByRole['Open'] = new Set();
          openShiftsByRole['Open'].add(name);
          if (!unmatchedRolesByName[name]) unmatchedRolesByName[name] = new Set();
          unmatchedRolesByName[name].add(role);
        }
      });
    });

    const rows = [];
    // Sort roles by roleOrder, then any remaining roles alphabetically
    const sortedRoles = Object.keys(roleGroups).sort((a, b) => {
      const ai = roleOrder.indexOf(a);
      const bi = roleOrder.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });
    sortedRoles.forEach(role => {
      const members = roleGroups[role];
      rows.push({ type: 'header', role });
      members.forEach(member => {
        rows.push({ type: 'member', member, role });
      });
      // Append open shift rows (Stint/TBC) that belong to this role group
      if (openShiftsByRole[role]) {
        openShiftsByRole[role].forEach(name => {
          rows.push({
            type: 'member',
            member: { Name: name, Role_Type: role, Role_Name: name },
            role,
            isOpen: true,
            openFilterRole: role,
          });
        });
      }
    });

    // Catch-all for open shifts that didn't match any role group
    if (openShiftsByRole['Open']) {
      rows.push({ type: 'header', role: 'Open' });
      openShiftsByRole['Open'].forEach(name => {
        rows.push({
          type: 'member',
          member: { Name: name, Role_Type: 'Open', Role_Name: name },
          role: 'Open',
          isOpen: true,
          openFilterRoles: unmatchedRolesByName[name] ? [...unmatchedRolesByName[name]] : null,
        });
      });
    }

    return rows;
  }, [team, memberSchedule, roleOrder]);

  const timeSlots = useMemo(() => {
    const allShifts = [...displayShapeOfDay, ...scheduledShifts];
    if (allShifts.length === 0) return [];
    const startTime = Math.floor(Math.min(...allShifts.map(item => parseTime(item.start))));
    const endTime = Math.ceil(Math.max(...allShifts.map(item => parseTime(item.end))));
    const timeSlots = Array.from({length: (endTime - startTime) * 2 + 1}, (_, i) => startTime + i * 0.5);
    return timeSlots;
  }, [displayShapeOfDay, scheduledShifts, parseTime]);

  const handleGenerateSchedule = () => {
    console.log('Generating schedule...');
    
    const processData = (shapeOfDay, team, config, events) => {
      if (!shapeOfDay || !team) return null;
      
      const reducedShapeOfDay = shapeOfDay.map(({ day, end, length, start, type, date, shape_of_day_type }) => ({
        day,
        end,
        length,
        start,
        type,
        date,
        shape_of_day_type,
      }));
      const activeTeam = team.filter(member => member.Active !== false);
      const updatedTeam = activeTeam.map(member => ({
        ...member,
        Changed_Expected_Hours: Math.round(member.Changed_Expected_Hours),
        Weekly_Expected_Hours: Math.round(member.Weekly_Expected_Hours),
      }));
      
      // parseCSV maps empty cells to undefined, which JSON.stringify drops. Open
      // non-service shifts have no employee_id/name, so restore those required
      // fields explicitly (null assignee, empty name) before sending.
      const normalizedEvents = (events || []).map(event => ({
        ...event,
        employee_id: event.employee_id ?? null,
        name: event.name ?? '',
      }));

      return {
        shape_of_day: reducedShapeOfDay,
        team: updatedTeam,
        config: config,
        events: normalizedEvents,
        hourly_forecasts: hourlyForecasts || undefined,
        custom_rules: customRules,
        schedulingLinks: schedulingLinks.length > 0 ? schedulingLinks : undefined,
        labour_bands: labourBands || undefined,
        // camelCase to match ScheduleRequest.breakRules. Omitted when absent so
        // apply_break_rules() no-ops and the config's BREAKS arrays stay authoritative.
        breakRules: breakRules?.length ? breakRules : undefined,
      };
    };

    const data = processData(shapeOfDay, team, config, events);

    if (!data) {
      setGenerateError("No data available to generate schedule.");
      return;
    }

    console.log(data);
    setIsGeneratingSchedule(true);
    setGenerateError(null);

    const fetchWithRetry = (url) => {
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }).then(async response => {
        if (!response.ok) {
          let detail = '';
          try {
            const body = await response.json();
            detail = typeof body.detail === 'string'
              ? body.detail
              : JSON.stringify(body.detail || body);
          } catch (e) {
            detail = await response.text().catch(() => '');
          }
          console.error(`Backend ${response.status} response:`, detail);
          throw new Error(`HTTP error! status: ${response.status}${detail ? ` — ${detail}` : ''}`);
        }
        return response.json();
      });
    };

    const url = dev ?'http://127.0.0.1:8010/generate_schedule_audit':'https://auto-scheduler.goattest.io/generate_schedule_audit'

    console.log(`Generating schedule data from ${url}`);
    fetchWithRetry(url)
      .then(data => {
        console.log('Success:', data);
        setScheduledShifts(data.schedule);
        setScheduleAssignmentReasons(data.assignment_reasons);
        setUnfilledKeyHolderShifts(data.reasoning?.summary?.unfilled_key_holder_shifts || []);
        setKeyHolderRescues(data.reasoning?.summary?.telemetry?.key_holder_rescues || []);
        setUnfilledSectionShifts(data.reasoning?.summary?.unfilled_section_shifts || []);
        setSectionKeyHolderRescues(data.reasoning?.summary?.telemetry?.section_key_holder_rescues || []);
        setContractedShortfall(data.assignment_reasons?.summary?.telemetry?.contracted_hours_shortfall || []);
        setStretchProfile(data.assignment_reasons?.summary?.telemetry?.stretchProfile || null);
        setSkillMixFloor(data.reasoning?.summary?.telemetry?.skill_mix_floor || data.assignment_reasons?.summary?.telemetry?.skill_mix_floor || null);
        setSetShiftOutcomes(data.set_shift_outcomes || []);
        setRedistribution(data.redistribution || []);
        setScheduleScores(data.schedule_score);
        setScheduleLoaded(true);
        setBusyPeriods(data.busy_periods);
        setCoverage(data.coverages?.coverages_by_day || {});
        setTotalCoverage(data.coverages?.total_coverage || { under: 0, over: 0, correct: 0 });
        setBreaks(data.breaks);
        if (data.customRuleResults) {
          setCustomRuleResults(data.customRuleResults);
        }

        // The audit re-clamps server-side; its deltas supersede the fixture's (e.g. after
        // the user edits demand and regenerates).
        const bandTelemetry = data.reasoning?.summary?.telemetry?.labour_bands;
        if (bandTelemetry?.deltas) {
          setLabourBands((prev) => ({ ...(prev || {}), deltas: bandTelemetry.deltas }));
        }

        // Compare per-slot coverage: input vs output headcount per 30-min slot
        const parseT = (v) => {
          if (typeof v === 'string' && v.includes(':')) {
            const [h, m] = v.split(':').map(Number);
            return h + m / 60;
          }
          return parseFloat(v);
        };
        const buildSlotCoverage = (shifts, getType) => {
          const coverage = {}; // "day|sdt" -> {slot: count}
          shifts.forEach(s => {
            const sdt = getType(s);
            if (!sdt) return;
            const key = `${s.day}|${sdt}`;
            if (!coverage[key]) coverage[key] = {};
            const start = parseT(s.start);
            const end = parseT(s.end);
            // Align to the 30-min grid; off-grid shifts (e.g. 17:45 starts)
            // contribute fractional occupancy to their edge slots
            for (let slot = Math.floor(start * 2) / 2; slot < end; slot += 0.5) {
              const overlap = Math.min(end, slot + 0.5) - Math.max(start, slot);
              coverage[key][slot] = (coverage[key][slot] || 0) + overlap / 0.5;
            }
          });
          return coverage;
        };
        // Non-service shifts (events: stock, admin, training…) don't serve demand, so they
        // must not count as coverage — a 07:00 "Bar stocks" event is not Bar overage.
        const serviceShifts = data.schedule.filter(s => !s.event_name && !s.event_type);
        const inputSlots = buildSlotCoverage(shapeOfDay, s => s.shape_of_day_type);
        const outputSlots = buildSlotCoverage(serviceShifts, s => s.shape_of_day_type);

        // Build per-slot diff map and total hours diff
        const slotDiffMap = {};
        const hoursDiffMap = {};
        const allKeys = new Set([...Object.keys(inputSlots), ...Object.keys(outputSlots)]);
        allKeys.forEach(key => {
          const inp = inputSlots[key] || {};
          const out = outputSlots[key] || {};
          const allSlots = new Set([...Object.keys(inp), ...Object.keys(out)]);
          let totalDiffHours = 0;
          const slotDiffs = {};
          allSlots.forEach(slot => {
            const diff = Math.round(((out[slot] || 0) - (inp[slot] || 0)) * 100) / 100;
            if (diff !== 0) {
              slotDiffs[slot] = diff;
              totalDiffHours += diff * 0.5;
            }
          });
          if (Object.keys(slotDiffs).length > 0) {
            slotDiffMap[key] = slotDiffs;
            hoursDiffMap[key] = Math.round(totalDiffHours * 10) / 10;
          }
        });
        setHeadcountDiffMap(hoursDiffMap);
        setSlotDiffMap(slotDiffMap);
        if (Object.keys(hoursDiffMap).length > 0) {
          console.log('Coverage diffs (hours):', hoursDiffMap);
        } else {
          console.log('Coverage matches input for all shape_of_day_types');
        }

        // Calculate assigned hours for each team member
        const assignedHours = team.reduce((acc, member) => {
          acc[member.Name] = 0;
          return acc;
        }, {});
  
        data.schedule.forEach(shift => {
          if (shift.name !== "Stint") {
            assignedHours[shift.name] += parseFloat(shift.length);
          }
        });
  
        // Update teams with assigned hours
        const updateTeamWithHours = (team) => team.map(member => ({
          ...member,
          Assigned_Hours: assignedHours[member.Name] || 0
        }));
  
        setTeam(updateTeamWithHours(team));

        // Auto-expand sections after schedule generation
        setIsRotaExpanded(true);
        setIsTeamExpanded(true);
        setIsScoresExpanded(true);
        setIsShapeOfDayExpanded(true);

      })
      .catch((error) => {
        console.error('Error:', error);
        setGenerateError(error.message);
      })
      .finally(() => {
        setIsGeneratingSchedule(false);
      });
  };

  const handleShiftClick = (shift) => {
    console.log('Shift clicked:', shift);
    setSelectedShift(shift);
  };

  const renderTotalCoverage = () => {
    return (
      <div className="total-coverages">
        {scheduleGenerated && (
          <div className="total-coverage-summary">
            <h2>Total Coverage</h2>
            <span className="coverage-under">Under: {totalCoverage.under}%</span>
            <span className="coverage-over">Over: {totalCoverage.over}%</span>
            <span className="coverage-correct">Correct: {totalCoverage.correct}%</span>
          </div>
        )}
      </div>
    );
  };

  const renderSchedule = () => {
    // Extract unique role combinations from shape of day
    const uniqueRoles = [...new Set(displayShapeOfDay.map(shift => shift.shape_of_day_type))];
    const currencySym = ({ GBP: '£', EUR: '€', USD: '$' })[forecast?.currency] || (forecast?.currency ? forecast.currency : '£');
    const dayForecast = (day) => {
      const d = day.toLowerCase();
      const v = forecast?.[d] ?? forecast?.by_day?.[d];
      if (v == null || !Number.isFinite(Number(v))) return null;
      return `${currencySym}${Math.round(Number(v)).toLocaleString('en-GB')}`;
    };
    return orderedDays.map(day => (
      organizedSchedule[day] && (
        <div key={day} className="day-schedule">
          <h1 className="day-title">
            {day}
            {dayForecast(day) && (
              <span style={{ marginLeft: 12, fontSize: '0.6em', fontWeight: 400, color: '#6b7280' }}>
                {dayForecast(day)}
              </span>
            )}
          </h1>
          {scheduleLoaded && (
            <div className="coverage-summary">
              <span className="coverage-under">Under: {coverage[day]?.under}%</span>
              <span className="coverage-over">Over: {coverage[day]?.over}%</span>
              <span className="coverage-correct">Correct: {coverage[day]?.correct}%</span>
            </div>
          )}
          {scheduleLoaded && stretchProfile && (() => {
            const key = Object.keys(stretchProfile).find(k => k.toLowerCase() === String(day).toLowerCase());
            return key ? (
              <StretchDayStrip dayProfile={stretchProfile[key]} timeSlots={timeSlots} parseTime={parseTime} />
            ) : null;
          })()}
          {uniqueRoles.map(shape_of_day_type => {
            const slotDiffs = slotDiffMap[`${day}|${shape_of_day_type}`];
            const netHours = headcountDiffMap[`${day}|${shape_of_day_type}`] || 0;
            return scheduleGenerated && (
              <div key={`${day}-${shape_of_day_type}`}>
                
                {organizedSchedule[day][shape_of_day_type] && organizedSchedule[day][shape_of_day_type].length > 0 && (
                  <>
                    <div className={`role-schedule`}>
                      <h3 className={`role-title`}>
                        {displayCompositeRole(shape_of_day_type, partnerId)}
                        {scheduleLoaded && netHours !== 0 && (
                          <span
                            className={netHours > 0 ? 'headcount-badge-over' : 'headcount-badge-under'}
                            title="Net scheduled hours vs shape of day"
                          >
                            {netHours > 0 ? '+' : ''}{netHours}h net
                          </span>
                        )}
                      </h3>
                      <div className="schedule-grid">
                        {organizedSchedule[day][shape_of_day_type].slice().reverse().map((row, rowIndex) => (
                          <div key={`${shape_of_day_type}-${rowIndex}`} className="schedule-row">
                            <div className="row-label">Staff {organizedSchedule[day][shape_of_day_type].length - rowIndex}</div>
                            <div className="shifts-container">
                              {row.map((shift, shiftIndex) => renderShift(shift, `${shape_of_day_type}-${rowIndex}-${shiftIndex}`, shape_of_day_type))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {scheduleLoaded && skillMixFloor && skillMixFloor.active && (
                      <SkillFloorDayStrip
                        breaches={(skillMixFloor.breaches || []).filter(
                          (b) =>
                            String(b.day || '').toLowerCase() === String(day).toLowerCase() &&
                            String(b.shape || '') === String(shape_of_day_type)
                        )}
                        timeSlots={timeSlots}
                        parseTime={parseTime}
                        target={skillMixFloor.target}
                      />
                    )}
                    {scheduleLoaded && slotDiffs && (
                      <div className="coverage-diff-row">
                        <div className="row-label">Over/Under</div>
                        <div className="shifts-container">
                          {Object.entries(slotDiffs).map(([slot, diff]) => {
                            const slotTime = parseFloat(slot);
                            const startPct = ((slotTime - timeSlots[0]) / (timeSlots[timeSlots.length - 1] - timeSlots[0])) * 100;
                            const widthPct = (0.5 / (timeSlots[timeSlots.length - 1] - timeSlots[0])) * 100;
                            return (
                              <div
                                key={slot}
                                className={`coverage-diff-slot ${diff > 0 ? 'diff-over' : 'diff-under'}`}
                                style={{ left: `${startPct}%`, width: `${widthPct}%` }}
                                title={`${Math.floor(slotTime)}:${(slotTime % 1) * 60 === 0 ? '00' : '30'} — ${diff > 0 ? `+${diff} over required` : `${diff} under required`}`}
                              >
                                {diff > 0 ? '+' : ''}{diff}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {bandDeltaMap[`${day}|${shape_of_day_type}`] && ['added', 'removed'].map((dir) => {
                      const segs = bandDeltaMap[`${day}|${shape_of_day_type}`].filter((d) => d.direction === dir);
                      if (!segs.length) return null;
                      const added = dir === 'added';
                      return (
                        <div key={dir} className="coverage-diff-row">
                          <div className="row-label">{added ? 'Band +' : 'Band −'}</div>
                          <div className="shifts-container">
                            {segs.map((d, i) => {
                              const start = parseTime(d.start);
                              const end = parseTime(d.end);
                              const startPct = ((start - timeSlots[0]) / (timeSlots[timeSlots.length - 1] - timeSlots[0])) * 100;
                              const widthPct = ((end - start) / (timeSlots[timeSlots.length - 1] - timeSlots[0])) * 100;
                              return (
                                <div
                                  key={i}
                                  className={`band-delta-slot ${added ? 'band-added' : 'band-removed'}`}
                                  style={{ left: `${startPct}%`, width: `${widthPct}%` }}
                                  title={`${d.start}–${d.end} — ${added
                                    ? `band min adds +${d.heads} on top of the original demand`
                                    : `band max removes −${d.heads} from the original demand`}`}
                                >
                                  {added ? '+' : '−'}{d.heads}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    {renderBusyPeriods(day, shape_of_day_type)}
                    {renderBreaks(day, shape_of_day_type)}
                  </>
                )}
              </div>
            )
          })}
        </div>
      )
    ));
  };
  
  const renderShift = (shift, key, role) => {
    // if (!shift) return null; // Return null if shift is undefined
    
    // Hide shape of day shifts when schedule is loaded
    if (shift.isShapeOfDay && scheduleLoaded) {
      return null;
    }
  
    const startPercentage = ((parseTime(shift.start) - timeSlots[0]) / (timeSlots[timeSlots.length - 1] - timeSlots[0])) * 100;
    const widthPercentage = (shift.length / (timeSlots[timeSlots.length - 1] - timeSlots[0])) * 100;
  
    let shiftText = '';
    let shiftClass = 'shift';
  
    if (shift.shift_type) {
      shiftClass += ` ${shift.type.toLowerCase()}`;
      if (shift.role !== 'Stint' && shift.role !== 'TBC') {
        shiftText += `${shift.name} (${displayRole(shift.role, partnerId)})`;
      } else {
        shiftText += shift.role;
      }
    } else if (shift.type) {
      shiftClass += ` ${shift.type.toLowerCase()}`;
      shiftText += shift.type;
    } else {
      shiftText += 'Unknown';
    }
  
    if (shift.isShapeOfDay) {
      // The demand grid draws the ORIGINAL (pre-band) demand; the band's changes
      // render as their own Band +/− rows rather than tinting blocks here.
      shiftClass += ' shape-of-day';
    } else {
      shiftClass += ' scheduled';
      // Any Manager role (Manager, Shift Manager, Kitchen Manager, etc.) renders yellow.
      const roleStr = typeof shift.role === 'string' ? shift.role.toLowerCase() : '';
      if (roleStr.includes('manager')) {
        shiftClass += ' manager';
      } else if (Array.isArray(shift.shift_type)) {
        if (shift.shift_type.includes('Server')) {
          shiftClass += ' server';
        } else if (shift.shift_type.includes('Manager')) {
          shiftClass += ' manager';
        }
        else if (shift.shift_type?.length === 1 && shift.shift_type[0] === 'Shift Manager') {
          shiftClass += ' manager';
        }
        else if (shift.shift_type.includes('Pizzaiolo')) {
          shiftClass += ' pizzaiolo';
        }
        else if (shift.shift_type.includes('Cleaner')) {
          shiftClass += ' cleaner';
        }
      } else if (typeof shift.shift_type === 'string') {
        shiftClass += ' ' + shift.shift_type.toLowerCase();
      }
    }
  
    shiftText += ` ${shift.startDisplay} - ${shift.endDisplay} (${shift.length.toFixed(1)}h)`;

    // A scheduled block that honours one of the member's set shifts gets the same pin the rota grid
    // shows, so the pinned window and the residual beside it read together on the Shape of Day.
    const setShiftOutcome = !shift.isShapeOfDay && shift.employee_id
      ? honouredSetShiftLookup.get(`${shift.employee_id}|${shift.day}|${shift.startDisplay}`)
      : null;
    if (setShiftOutcome) {
      shiftClass += ' set-shift';
      shiftText += ` — Set shift (${setShiftOutcome.constraint_type})`;
    }

    const shiftBreak = breaks && 
    Object.entries(breaks).find(([breakKey, _]) => {
      // const [ShiftDay, ShiftStart, ShiftEnd, ShiftName] = breakKey.split('_');
      // console.log("Break:", ShiftDay, ShiftStart, ShiftEnd, ShiftName);
      // console.log("Shift:", shift.day, shift.startDisplay, shift.endDisplay, shift.name);
      return breakKey === shift.shift_id;
    });

  let breakElement = null;
  if (shiftBreak) {
    const [breakKey, breakSlot] = shiftBreak;
    const shiftStartTime = parseTime(shift.start);
    const breakStartTime = parseTime(breakSlot[0].start);
    const breakEndTime = parseTime(breakSlot[0].end);
    
    // Calculate break position and width relative to the shift
    const breakStartOffset = breakStartTime - shiftStartTime;
    const breakDuration = breakEndTime - breakStartTime;
    
    const breakStartPercentage = (breakStartOffset / shift.length) * 100;
    const breakWidthPercentage = (breakDuration / shift.length) * 100;

    // console.log("Break:", breakSlot.start, "Shift:", shift.start, "Parse:", parseTime(breakSlot.start), "-", parseTime(shift.start), "Percentage:", breakStartPercentage, "-", breakWidthPercentage, "Shift Percentage:", startPercentage, "-", widthPercentage);

    // console.log("Break:", breakStartTime,"-", breakEndTime, "Percentage:", breakStartPercentage,"-", breakWidthPercentage, "Slot:", breakSlot, "Shift:", ShiftStart, "-", ShiftEnd, "parse:", parseTime(ShiftStart), "-" , parseTime(ShiftEnd));
    // console.log("Length:", shift.length, breakSlot.length);

    breakElement = (
      <div
        className="break-period"
        style={{
          position: 'absolute',
          left: `${breakStartPercentage}%`,
          width: `${breakWidthPercentage}%`,
          height: '100%',
          backgroundColor: '#bf11076b',
          zIndex: 1,
        }}
        title={`Break: ${breakSlot[0].start} - ${breakSlot[0].end}`}
      />
    );
  }
  
  return (
    <div
      key={key}
      className={shiftClass}
      style={{
        // position: 'relative',
        left: `${startPercentage}%`,
        width: `${widthPercentage}%`,
      }}
      onClick={() => handleShiftClick(shift)}
      title={shiftText}
    >
      {breakElement}
      <div className="shift-content">
        {shiftText}
      </div>
      {setShiftOutcome && (
        <span
          className="shift-set-shift-pin"
          role="img"
          aria-label={`Set shift (${setShiftOutcome.constraint_type})`}
          title={`Set shift (${setShiftOutcome.constraint_type})`}
        >
          📌
        </span>
      )}
    </div>
  );
};

const renderBusyPeriods = useCallback((day, shape_of_day_type) => {
  if (!busyPeriods || !busyPeriods['day'] || !busyPeriods['week']) {
    return null;
  }

  const day_busy_periods = busyPeriods['day'][day][shape_of_day_type] || [];
  const week_busy_periods = busyPeriods['week'][day][shape_of_day_type] || [];

  const renderPeriods = (periods, color, type) => {
    return periods.map((period, index) => {
      const [start, end] = period;
      const startTime = parseTime(start);
      const endTime = parseTime(end);
      const startPercentage = ((startTime - timeSlots[0]) / (timeSlots[timeSlots.length - 1] - timeSlots[0])) * 100;
      const widthPercentage = ((endTime - startTime) / (timeSlots[timeSlots.length - 1] - timeSlots[0])) * 100;

      return (
        <div
          key={`busy-${day}-${type}-${index}`}
          className={`busy-period ${type}`}
          style={{
            position: 'absolute',
            left: `${startPercentage}%`,
            width: `${widthPercentage}%`,
            height: '100%',
            backgroundColor: color,
          }}
          title={`${type} Busy Period: ${start} - ${end}`}
        />
      );
    });
  };

  return (
    <div className={`busy-periods-container`}>
      <div className="busy-period-row">
        <div className="busy-periods-legend">
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: 'orange' }}></div>
            <span className="legend-label">Daily</span>
          </div>
        </div>
        <div className="day-busy-periods">
          {renderPeriods(day_busy_periods, 'orange', 'Daily')}
        </div>
      </div>
      <div className="busy-period-row">
        <div className="busy-periods-legend">
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: 'brown' }}></div>
            <span className="legend-label">Weekly</span>
          </div>
        </div>
        <div className="week-busy-periods">
          {renderPeriods(week_busy_periods, 'brown', 'Weekly')}
        </div>
      </div>
    </div>
  );
}, [busyPeriods, timeSlots, parseTime]);

  const renderBreaks = (day, area) => {
    if (!day || !area || !breaks[area]) {
      return null;
    }
    console.log('Breaks:', breaks);
    console.log('Day:', day, 'Area:', area);

    const areaBreaks = breaks[area];
    if (typeof areaBreaks !== 'object' || areaBreaks === null) {
      console.warn(`No breaks found for ${area}`);
      return null;
    }

    const dayBreaks = Object.entries(areaBreaks).filter(([key, _]) => {
      const [breakDay] = key.split('_');
      return breakDay === day;
    });

    return dayBreaks.map(([key, breakSlot], index) => {
      const [_, start, end, name] = key.split('_');
      const startTime = parseTime(start);
      const endTime = parseTime(end);
      const breakTime = startTime + (breakSlot * 0.5); // Convert slot to time
      const breakStartPercentage = ((breakTime - timeSlots[0]) / (timeSlots[timeSlots.length - 1] - timeSlots[0])) * 100;

      return (
        <div
          key={`break-${day}-${area}-${index}`}
          className="break-indicator"
          style={{
            left: `${breakStartPercentage}%`,
          }}
          title={`Break for ${name}: ${formatTime(breakTime)}`}
        >
          <Coffee size={16} />
        </div>
      );
    });
  };

  const handleDownloadConfig = useCallback(() => {
    const configContent = JSON.stringify(config, null, 2);
    const blob = new Blob([configContent], { type: 'application/json;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'config.json');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [config]);

  const handleDownloadCustomRules = useCallback(() => {
    const blob = new Blob([JSON.stringify(customRules, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'custom_rules.json';
    a.click();
    URL.revokeObjectURL(url);
  }, [customRules]);

  const handleRunTestRequest = useCallback(async () => {
    console.log('Running test request...');

    // Validate all fields before proceeding
    if (!validateTestRequestFields()) {
      const errorMessage = Object.values(validationErrors).join(', ');
      setGenerateError(`Cannot run test request: ${errorMessage}`);
      return;
    }

    // Format timestamps in ISO format as UTC
    const formatTimestamp = (date, time) => {
      // Treat the input time as UTC to avoid timezone conversion
      return `${date}T${time}:00.000Z`;
    };

    const testRequestPayload = {
      timestamp_from: formatTimestamp(fromDate, fromTime),
      timestamp_to: formatTimestamp(toDate, toTime),
      partner_node_id: isManualEntry ? manualPartnerNodeId.trim() : selectedSite, 
      week_commencing_date: selectedWeek,
    };

    console.log('Test Request Payload:', testRequestPayload);
    setIsGeneratingSchedule(true);
    setGenerateError(null);

    const url = dev ? 'http://127.0.0.1:8010/generate_schedule_test' : 'https://auto-scheduler.goattest.io/generate_schedule_test' 

    console.log(`Running test request to ${url}`);
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testRequestPayload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.error) {
        setGenerateError(data.error);
        return;
      }
      console.log('Test request successful:', data);
      
      if (data.schedule) {
        setScheduleGenerated(true);
        const derivedOrder = deriveOrderedDaysFromShape(data.shape_of_day);
        setOrderedDays(derivedOrder || data.config.ORDERED_DAYS);
        setScheduleLoaded(true);
        setShapeOfDay(data.shape_of_day);
        setTeam(data.team);
        setConfig(data.config);
        setScheduledShifts(data.schedule);
        setScheduleAssignmentReasons(data.assignment_reasons || {});
        setUnfilledKeyHolderShifts(data.reasoning?.summary?.unfilled_key_holder_shifts || []);
        setKeyHolderRescues(data.reasoning?.summary?.telemetry?.key_holder_rescues || []);
        setUnfilledSectionShifts(data.reasoning?.summary?.unfilled_section_shifts || []);
        setSectionKeyHolderRescues(data.reasoning?.summary?.telemetry?.section_key_holder_rescues || []);
        setContractedShortfall(data.reasoning?.summary?.telemetry?.contracted_hours_shortfall || []);
        setSkillMixFloor(data.reasoning?.summary?.telemetry?.skill_mix_floor || data.assignment_reasons?.summary?.telemetry?.skill_mix_floor || null);
        setSetShiftOutcomes(data.set_shift_outcomes || []);
        setScheduleScores(data.schedule_score || {});
        setBusyPeriods(data.busy_periods || {});
        setCoverage(data.coverages?.coverages_by_day || {});
        setTotalCoverage(data.coverages?.total_coverage || { under: 0, over: 0, correct: 0 });
        setBreaks(data.breaks || {});
  
        // Calculate assigned hours for each team member
        if (team.length > 0) {
          const assignedHours = team.reduce((acc, member) => { 
            acc[member.Name] = 0;
            return acc;
          }, {});
    
          data.schedule.forEach(shift => {
            if (shift.name !== "Stint" && shift.name !== "TBC") {
              assignedHours[shift.name] = (assignedHours[shift.name] || 0) + parseFloat(shift.length);
            }
          });
    
          // Update teams with assigned hours
          const updateTeamWithHours = (team) => team.map(member => ({
            ...member,
            Assigned_Hours: assignedHours[member.Name] || 0
          }));
    
          setTeam(updateTeamWithHours(team));
        }
      } else {
        setGenerateError("The test request did not return a schedule.");
      }
    } catch (error) {
      console.error('Error:', error);
      setGenerateError(error.message);
    } finally {
      setIsGeneratingSchedule(false);
    }
  }, [partnerId, selectedWeek, fromDate, toDate, fromTime, toTime, team, validationErrors, validateTestRequestFields]);

  return (
    <partnerContext.Provider value = {partnerId}>
    <div className="schedule-dashboard">
      {isLoggedIn ? (
        <>
      <h1 className="dashboard-title">Schedule Dashboard</h1>
      
      <div className="file-upload-section">
        <div className="flex items-center space-x-2 mb-4">
            {/* <Switch
              id="stint-mode"
              checked={stintEnabled}
              onCheckedChange={setStintEnabled}
            />
            <Label htmlFor="stint-mode">With Stint</Label> */}
          </div>

          {partnerId === 'admin' && (
            <div className="flex items-center space-x-2 mb-4">
              <Switch
                id="debugging-mode"
                checked={isDebuggingMode}
                onCheckedChange={setIsDebuggingMode}
              />
              <Label htmlFor="debugging-mode">Debugging</Label>
            </div>
          )}

          {isDebuggingMode && (
          <div className="selection-controls-container">
            <div className="selection-controls">
              <div className="selection-row mb-2">
                <label htmlFor="site-select">Select Site</label>
                <select
                  id="site-select"
                  value={isManualEntry ? 'manual_entry' : selectedSite}
                  onChange={(e) => handleSiteChange(e.target.value)}
                  className={validationErrors.site ? "validation-error" : ""}
                >
                  <option value="">Select a site...</option>
                  <option value="manual_entry">Enter Partner Node ID manually</option>
                  {sites.map((site, index) => (
                    <option key={index} value={site}>{site}</option>
                  ))}
                  
                </select>
                {validationErrors.site && <div className="error-message">{validationErrors.site}</div>}
              </div>
              
              {isManualEntry && (
                <div className="selection-row mb-2">
                  <label htmlFor="manual-partner-id">Partner Node ID</label>
                  <input
                    type="text"
                    id="manual-partner-id"
                    value={manualPartnerNodeId}
                    onChange={(e) => setManualPartnerNodeId(e.target.value)}
                    placeholder="Enter partner node ID..."
                    className={validationErrors.site ? "validation-error" : ""}
                  />
                </div>
              )}
              
              <div className="selection-row mb-2">
                <label htmlFor="week-select">Week Commencing</label>
                <select
                  id="week-select"
                  value={selectedWeek}
                  onChange={(e) => setSelectedWeek(e.target.value)}
                  className={validationErrors.week ? "validation-error" : ""}
                >
                  <option value="">Select a week...</option>
                  {getRelevantWeeks().map((week, index) => (
                    <option key={index} value={week}>{week}</option>
                  ))}
                </select>
                {validationErrors.week && <div className="error-message">{validationErrors.week}</div>}
              </div>
              
              {validationErrors.dateRange && <div className="error-message date-range-error">{validationErrors.dateRange}</div>}
              {validationErrors.timeRange && <div className="error-message time-range-error">{validationErrors.timeRange}</div>}
              
              <div className="date-time-grid">
                <div className="selection-row date-row">
                  <label htmlFor="from-date" className="icon-label">
                    <Calendar size={14} /> From Date
                  </label>
                  <input
                    type="date"
                    id="from-date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={validationErrors.fromDate ? "validation-error" : ""}
                  />
                  {validationErrors.fromDate && <div className="error-message">{validationErrors.fromDate}</div>}
                </div>
                
                <div className="selection-row date-row">
                  <label htmlFor="to-date" className="icon-label">
                    <Calendar size={14} /> To Date
                  </label>
                  <input
                    type="date"
                    id="to-date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={validationErrors.toDate ? "validation-error" : ""}
                  />
                  {validationErrors.toDate && <div className="error-message">{validationErrors.toDate}</div>}
                </div>
                
                <div className="selection-row time-row">
                  <label htmlFor="from-time" className="icon-label">
                    <Clock size={14} /> From Time
                  </label>
                  <input
                    type="time"
                    id="from-time"
                    value={fromTime}
                    onChange={(e) => setFromTime(e.target.value)}
                    className={validationErrors.fromTime ? "validation-error" : ""}
                  />
                  {validationErrors.fromTime && <div className="error-message">{validationErrors.fromTime}</div>}
                </div>
                
                <div className="selection-row time-row">
                  <label htmlFor="to-time" className="icon-label">
                    <Clock size={14} /> To Time
                  </label>
                  <input
                    type="time"
                    id="to-time"
                    value={toTime}
                    onChange={(e) => setToTime(e.target.value)}
                    className={validationErrors.toTime ? "validation-error" : ""}
                  />
                  {validationErrors.toTime && <div className="error-message">{validationErrors.toTime}</div>}
                </div>
              </div>
            </div>
            
            <button 
              onClick={handleRunTestRequest} 
              className="test-request-btn"
              disabled={isGeneratingSchedule || Object.keys(validationErrors).length > 0}
              title={Object.keys(validationErrors).length > 0 ? Object.values(validationErrors).join(', ') : "Run test request"}
            >
              Run Test Request
            </button>
          </div>
          )}

          {!isDebuggingMode && (
          <>
          {availableShapes.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <Label style={{ whiteSpace: 'nowrap' }}>Week Shape:</Label>
              <Select value={selectedShape} onValueChange={(value) => setSelectedShape(value)}>
                <SelectTrigger style={{ width: '180px' }}>
                  <SelectValue placeholder="Select week" />
                </SelectTrigger>
                <SelectContent>
                  {availableShapes.map(shape => (
                    <SelectItem key={shape} value={shape}>
                      {shape.charAt(0).toUpperCase() + shape.slice(1).replace(/(\d)/, ' $1')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <button
            onClick={handleUploadDefaults}
            className="upload-defaults-btn"
          >
            <Upload className="mr-2" />
            Upload Defaults for Testing
          </button>
        
          {dev && (
          <button 
            onClick={handleUploadTestingData}
            className="upload-defaults-btn"
          >
            <Upload className="mr-2" />
            Upload Testing Data
          </button>
          )}
        
        <div className="file-upload-grid">
          <div className="file-upload-item">
            <label htmlFor="shape-of-day" className="file-upload-label">Shape of Day</label>
            <div className="file-input-wrapper">
              <input
                id="shape-of-day"
                type="file"
                onChange={(e) => handleFileUpload(e, shapeOfDay)}
                accept=".csv"
                className="file-input"
              />
              <label htmlFor="shape-of-day" className="file-input-button">
              <Upload className="mr-2" />
                Choose File
              </label>
            </div>
          </div>
          
          <div className="file-upload-item">
            <label htmlFor="team-data" className="file-upload-label">Team JSON</label>
            <div className="file-input-wrapper">
              <input
                id="team-data"
                type="file"
                onChange={(e) => handleFileUpload(e, team)}
                accept=".json"
                className="file-input"
              />
              <label htmlFor="team-data" className="file-input-button"><Upload className="mr-2" />Choose File</label>
            </div>
          </div>
        </div>
        </>
        )}
      </div>

      
          <div className="dashboard-buttons">
            {config && (
              <button onClick={handleDownloadConfig} className="download_button">
                <Download className="mr-2" />
                Download Config
              </button>
            )}
            {scheduleLoaded && (
            <button onClick={handleDownloadSchedule} className="download_button">
              <Download className="mr-2" />
              Download Schedule
            </button>
            )}
            <button 
              onClick={() => setIsLoggedIn(false)} 
              className="logout_button">
              Logout
            </button>
          </div>

        
      {scheduleLoaded && (
        <div className="fixed-buttons">
          <button onClick={handleReset}>Reset</button>
          <button onClick={handleBack} disabled={isInitialView}>Back</button>
          <button onClick={handlePlayPause}>{isPlaying ? 'Pause' : 'Play'}</button>
          <button onClick={handleForward} disabled={currentShiftIndex === scheduledShifts.length - 1 && !isInitialView}>Forward</button>
        </div>
      )}


      <button 
        onClick={handleGenerateSchedule}
        disabled={isGeneratingSchedule}
        className={`generate-schedule-button ${isGeneratingSchedule ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {isGeneratingSchedule ? (
          <>
            <Loader className="animate-spin mr-2"/>
            Generating Schedule...
          </>
        ) : (
          'Generate Schedule'
        )}
      </button>
      
      {generateError && (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>
            Failed to generate schedule: {generateError}. Please check your input data and try again.
          </AlertDescription>
        </Alert>
      )}

      <CustomRulesPanel
        customRules={customRules}
        setCustomRules={setCustomRules}
        onDownload={handleDownloadCustomRules}
        ruleResults={customRuleResults}
      />

      <SchedulingLinksPanel
        team={team}
        schedulingLinks={schedulingLinks}
        setSchedulingLinks={setSchedulingLinks}
        partnerId={partnerId}
      />

      {/* Rota & Shape of Day Sections */}
      {((shapeOfDay.length > 0) || visibleScheduledShifts.length > 0) ? (
        <>
          <div className="collapsible-section">
            <div className="collapsible-header" onClick={() => setIsRotaExpanded(!isRotaExpanded)}>
              <h2 className="collapsible-title">Rota</h2>
              {isRotaExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
            {isRotaExpanded && (
              <>
                {scheduleLoaded && team.length > 0 && (
                  <RotaComplianceTable
                    tableRows={tableRows}
                    memberSchedule={memberSchedule}
                    config={config}
                    partnerId={partnerId}
                    shapeOfDay={shapeOfDay}
                    slotDiffMap={slotDiffMap}
                    breaks={breaks}
                    bandDeltas={labourBands?.deltas || null}
                    setShiftOutcomes={setShiftOutcomes}
                    idealMatchEnabled={idealMatchEnabled}
                    setIdealMatchEnabled={setIdealMatchEnabled}
                    idealMatchBuffer={idealMatchBuffer}
                    setIdealMatchBuffer={setIdealMatchBuffer}
                    boundaryMatchEnabled={boundaryMatchEnabled}
                    setBoundaryMatchEnabled={setBoundaryMatchEnabled}
                    boundaryMatchBuffer={boundaryMatchBuffer}
                    setBoundaryMatchBuffer={setBoundaryMatchBuffer}
                    unfilledKeyHolderShifts={unfilledKeyHolderShifts}
                    keyHolderRescues={keyHolderRescues}
                    unfilledSectionShifts={unfilledSectionShifts}
                    sectionKeyHolderRescues={sectionKeyHolderRescues}
                    contractedShortfall={contractedShortfall}
                  />
                )}
                {scheduleLoaded && team.length > 0 && (
                  <ScheduleGrid
                    tableRows={tableRows}
                    memberSchedule={memberSchedule}
                    orderedDays={orderedDays}
                    onShiftClick={handleShiftClick}
                    partnerId={partnerId}
                    config={config}
                    shapeOfDay={shapeOfDay}
                    forecast={forecast}
                    idealMatchEnabled={idealMatchEnabled}
                    idealMatchBuffer={idealMatchBuffer}
                    boundaryMatchEnabled={boundaryMatchEnabled}
                    boundaryMatchBuffer={boundaryMatchBuffer}
                  />
                )}

                {scheduleLoaded && (
                  <SetShiftOutcomesPanel outcomes={setShiftOutcomes} employees={team} />
                )}

                {scheduleLoaded && (
                  <RedistributionPanel redistribution={redistribution} scheduledShifts={scheduledShifts} />
                )}

                {scheduleLoaded && (
                  <div className="collapsible-section" style={{ margin: '8px 0 0' }}>
                    <div className="collapsible-header" onClick={() => setIsScoresExpanded(!isScoresExpanded)}>
                      <h2 className="collapsible-title">Coverage & Scores</h2>
                      {isScoresExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                    {isScoresExpanded && (
                      <>
                        {renderTotalCoverage()}
                        <ScheduleScores scheduleScores={scheduleScores} scheduleGenerated={scheduleGenerated}/>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="collapsible-section">
            <div className="collapsible-header" onClick={() => setIsShapeOfDayExpanded(!isShapeOfDayExpanded)}>
              <h2 className="collapsible-title">Shape of Day</h2>
              {isShapeOfDayExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
            {isShapeOfDayExpanded && (
              <div className="schedule-container">
                <div className="time-slots">
                  {timeSlots.filter(time => time % 1 === 0).map(time => (
                    <div key={time} className="time-slot">
                      <div className="time-label">{`${time}:00`}</div>
                      <div className="time-line"></div>
                    </div>
                  ))}
                </div>
                <StretchLegend present={!!stretchProfile} />
                {renderSchedule()}
              </div>
            )}
          </div>
        </>
      ) : (
        <p>No schedule data loaded. Please upload CSV files for shape of day and JSON files for team data.</p>
      )}

      {/* Team Section */}
      <div className="collapsible-section">
        <div className="collapsible-header" onClick={() => setIsTeamExpanded(!isTeamExpanded)}>
          <h2 className="collapsible-title">Team</h2>
          {isTeamExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        {isTeamExpanded && (
          <>
            <TeamOverview
              team={team}
              setTeam={setTeam}
              recentlyChangedHours={recentlyChangedHours}
              selectedWeek={selectedWeek}
              partnerId={partnerId}
              config={config}
            />
          </>
        )}
      </div>

      <ConfigPanel config={config} setConfig={setConfig} isLight={false} setOrderedDays={setOrderedDays} roleOrder={roleOrder} setRoleOrder={setRoleOrder}/>

      {selectedShift && (
        <ShiftDetails
          shift={selectedShift}
          onClose={() => setSelectedShift(null)}
          schedule_assignment_reasons={scheduleAssignmentReasons}
          partnerId={partnerId}
        />
      )}
      </>
    ) : (
      <Login onLogin={handleLogin} />
    )}
    </div></partnerContext.Provider> 
  );
};

export default ScheduleDashboard;
